/**
 * Stale-while-revalidate strategy implementation
 *
 * This strategy:
 * 1. Returns cached response immediately if available (even if stale)
 * 2. Fetches a fresh response from the network in the background
 * 3. Updates the cache with the new response for next time
 *
 * Best for: Resources where having the most up-to-date version is good
 * but not critical (CSS, JS, JSON data)
 */
import { debugLog } from "../utils/cache-helpers";
import { generateCacheKey } from "../utils/hash-generator";

/**
 * Stale-while-revalidate strategy for fetch requests
 * @param request The request to handle
 * @param cacheName The name of the cache to use
 * @returns Promise resolving to the response
 */
export async function staleWhileRevalidateStrategy(
  request: Request,
  cacheName: string
): Promise<Response> {
  const cacheKey = generateCacheKey(request.url);

  try {
    const cache = await caches.open(cacheName);
    const cachedResponse = await cache.match(cacheKey);

    // Start network fetch in the background
    const fetchPromise = fetch(request)
      .then((networkResponse) => {
        // Cache the new response if it's valid
        if (networkResponse.ok && networkResponse.status < 400) {
          // Clone the response before caching it
          const responseToCache = networkResponse.clone();
          cache.put(cacheKey, responseToCache);
          debugLog(`Updated cache for ${request.url} in background`);
        }
        return networkResponse;
      })
      .catch((error) => {
        debugLog(`Background fetch failed for ${request.url}:`, error);
        // If the background fetch fails, we still have the cached response
        // so we can ignore this error
        return null;
      });

    // If we have a cached response, return it immediately
    if (cachedResponse) {
      debugLog(
        `Returning cached response for ${request.url} while revalidating`
      );
      // Revalidate in the background
      fetchPromise;
      return cachedResponse;
    }

    // If no cached response, wait for the network response
    debugLog(`No cached response for ${request.url}, waiting for network`);
    const networkResponse = await fetchPromise;

    if (!networkResponse) {
      throw new Error(`Failed to fetch ${request.url}`);
    }

    return networkResponse;
  } catch (error) {
    debugLog(
      `Error in stale-while-revalidate strategy for ${request.url}:`,
      error
    );
    throw error;
  }
}

/**
 * Stale-while-revalidate strategy for the Cache API
 * @param cache The cache object
 * @param url The URL to fetch
 * @param fetchOptions Options for the fetch request
 * @returns Promise resolving to the response
 */
export async function staleWhileRevalidateWithCacheAPI(
  cache: Cache,
  url: string,
  fetchOptions?: RequestInit
): Promise<Response> {
  const cacheKey = generateCacheKey(url);

  try {
    const cachedResponse = await cache.match(cacheKey);

    // Start network fetch in the background
    const fetchPromise = fetch(url, fetchOptions)
      .then((networkResponse) => {
        // Cache the new response if it's valid
        if (networkResponse.ok && networkResponse.status < 400) {
          // Clone the response before caching it
          const responseToCache = networkResponse.clone();
          cache.put(cacheKey, responseToCache);
          debugLog(`Updated cache for ${url} in background`);
        }
        return networkResponse;
      })
      .catch((error) => {
        debugLog(`Background fetch failed for ${url}:`, error);
        // If the background fetch fails, we still have the cached response
        // so we can ignore this error
        return null;
      });

    // If we have a cached response, return it immediately
    if (cachedResponse) {
      debugLog(`Returning cached response for ${url} while revalidating`);
      // Revalidate in the background
      fetchPromise;
      return cachedResponse;
    }

    // If no cached response, wait for the network response
    debugLog(`No cached response for ${url}, waiting for network`);
    const networkResponse = await fetchPromise;

    if (!networkResponse) {
      throw new Error(`Failed to fetch ${url}`);
    }

    return networkResponse;
  } catch (error) {
    debugLog(`Error in stale-while-revalidate strategy for ${url}:`, error);
    throw error;
  }
}

/**
 * Determine if a cached response is stale based on its age
 * @param response The cached response
 * @param staleTimeMs Time in milliseconds after which a response is considered stale
 * @returns Whether the response is stale
 */
export function isResponseStale(
  response: Response,
  staleTimeMs = 24 * 60 * 60 * 1000 // 1 day by default
): boolean {
  // Check for Date header
  const dateHeader = response.headers.get("date");
  if (!dateHeader) {
    // If no date header, consider it stale to be safe
    return true;
  }

  const responseDate = new Date(dateHeader).getTime();
  const now = Date.now();

  // If the response is older than staleTimeMs, it's stale
  return now - responseDate > staleTimeMs;
}
